import { Box, CircularProgress, Typography } from "@mui/material";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import "../../../utils/pdfWorker";
import "./pdfSearchHighlight.css";
import { gray } from "../../../theme/themePrimitives";

// IMPORTANT
// We use our own pre-transpiled copy of pdfjs-dist (see craco.config.js
// alias + scripts/transpile-pdfjs-legacy.js) because both the default build
// and pdfjs-dist's "legacy" build still contain JS features (Promise
// .withResolvers, class static blocks) missing on Safari/iPadOS before 17.4
// / 16.4 (mozilla/pdf.js#19347, #20899), which breaks the viewer on older
// tablets.
// If the version of react-pdf/pdfjs-dist changes, re-run:
//   node scripts/transpile-pdfjs-legacy.js
// to regenerate vendor/pdfjs/pdf.mjs and public/pdf.worker.min.mjs.

export interface PdfViewerHandle {
  goToNextMatch: () => void;
  goToPrevMatch: () => void;
}

export interface SearchMatchStats {
  total: number;
  // 1-based index of the active match, 0 when there are no matches.
  current: number;
}

export interface PdfViewerProps {
  url: string;
  // When set, renders only this page instead of the full continuous scroll.
  pageNumber?: number;
  onDocumentLoad?: (numPages: number) => void;
  onPageRenderSuccess?: () => void;
  // Continuous-scroll mode only: fires once the last page scrolls into view.
  onReachedEnd?: () => void;
  // Continuous-scroll mode only: fires with whichever page is currently
  // most visible as the learner scrolls.
  onVisiblePageChange?: (page: number) => void;
  // Continuous-scroll mode only: keyword to highlight across all pages.
  searchTerm?: string;
  onSearchMatchesChange?: (stats: SearchMatchStats) => void;
}

const MAX_PAGE_WIDTH = 900;
// react-pdf renders each page's canvas at scale * devicePixelRatio, and every
// page is mounted at once (no virtualization) - on Retina displays that's 4x
// the memory per page (devicePixelRatio 2), which runs constrained tablets
// out of memory partway through long documents. Cap it well short of that.
const MAX_DEVICE_PIXEL_RATIO = 1.5;
const PAGE_HORIZONTAL_PADDING = 32;
// Fraction into the viewport height where the "current page" reference line sits.
const PAGE_REFERENCE_LINE_FRACTION = 0.15;

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const PdfViewer = forwardRef<PdfViewerHandle, PdfViewerProps>(
  (
    {
      url,
      pageNumber,
      onDocumentLoad,
      onPageRenderSuccess,
      onReachedEnd,
      onVisiblePageChange,
      searchTerm,
      onSearchMatchesChange,
    },
    ref,
  ) => {
    const [numPages, setNumPages] = useState<number | null>(null);
    const [lastPageRendered, setLastPageRendered] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [containerWidth, setContainerWidth] = useState<number | null>(null);
    const [searchMatches, setSearchMatches] = useState<HTMLElement[]>([]);
    const [activeMatchIndex, setActiveMatchIndex] = useState(0);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const observer = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width;
        if (width) setContainerWidth(width);
      });
      observer.observe(container);
      return () => observer.disconnect();
    }, []);

    const pageWidth = containerWidth
      ? Math.min(containerWidth - PAGE_HORIZONTAL_PADDING, MAX_PAGE_WIDTH)
      : undefined;

    // react-pdf renders each page as a small loading placeholder before its
    // canvas grows to full height, so numPages/pageWidth alone don't mean the
    // document has its real layout yet — reset this whenever a new document
    // (re)provides those, and only trust the sentinel once the last page has
    // actually rendered.
    useEffect(() => {
      setLastPageRendered(false);
    }, [numPages, pageWidth]);

    // Continuous-scroll mode: observe a sentinel placed after the last page to
    // detect the learner has scrolled to the end of the document. Gated on
    // lastPageRendered so it can't fire while pages are still growing from
    // their loading placeholders to full height (which would otherwise make
    // the sentinel appear in view before there's anything to scroll past).
    useEffect(() => {
      if (
        pageNumber != null ||
        !numPages ||
        pageWidth == null ||
        !onReachedEnd ||
        !lastPageRendered
      )
        return;
      const sentinel = sentinelRef.current;
      if (!sentinel) return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            onReachedEnd();
          }
        },
        { root: containerRef.current, threshold: 0.1 },
      );
      observer.observe(sentinel);
      return () => observer.disconnect();
    }, [pageNumber, numPages, pageWidth, onReachedEnd, lastPageRendered]);

    // Continuous-scroll mode: report the "current" page for a live "Page X
    // of N" indicator, using reading order (a page becomes current once its
    // top nears the viewport top) rather than pixel-majority, which flips
    // too early when multiple pages fit on screen. PAGE_REFERENCE_LINE_FRACTION
    // trims that threshold so it advances slightly before a page fully
    // clears. Last page is a special case since it may be shorter than the
    // viewport and never reach the reference line. Gated on lastPageRendered
    // like the sentinel above, since offsetTop drifts while earlier pages
    // are still growing to full height.
    useEffect(() => {
      if (
        pageNumber != null ||
        !numPages ||
        pageWidth == null ||
        !onVisiblePageChange ||
        !lastPageRendered
      )
        return;
      const container = containerRef.current;
      if (!container) return;

      const computeVisiblePage = () => {
        const scrollTop = container.scrollTop;
        const referenceLine =
          scrollTop + container.clientHeight * PAGE_REFERENCE_LINE_FRACTION;
        const atBottom =
          scrollTop + container.clientHeight >= container.scrollHeight - 1;

        let current = 1;
        for (let page = 1; page <= numPages; page++) {
          const el = pageRefs.current[page];
          if (!el) continue;
          const reached = el.offsetTop <= referenceLine;
          if (reached || (page === numPages && atBottom)) {
            current = page;
          } else {
            break;
          }
        }
        onVisiblePageChange(current);
      };

      let frame: number | null = null;
      const handleScroll = () => {
        if (frame != null) return;
        frame = requestAnimationFrame(() => {
          frame = null;
          computeVisiblePage();
        });
      };

      computeVisiblePage();
      container.addEventListener("scroll", handleScroll);
      return () => {
        container.removeEventListener("scroll", handleScroll);
        if (frame != null) cancelAnimationFrame(frame);
      };
    }, [
      pageNumber,
      numPages,
      pageWidth,
      onVisiblePageChange,
      lastPageRendered,
    ]);

    const textRenderer = useCallback(
      (textItem: { str: string }) => {
        if (!searchTerm) return textItem.str;
        const pattern = new RegExp(escapeRegExp(searchTerm), "gi");
        return textItem.str.replace(
          pattern,
          (match) => `<mark class="pdf-search-mark">${match}</mark>`,
        );
      },
      [searchTerm],
    );

    const recomputeMatches = useCallback(() => {
      const container = containerRef.current;
      if (!container) return;
      const found = Array.from(
        container.querySelectorAll<HTMLElement>("mark.pdf-search-mark"),
      );
      setSearchMatches(found);
      setActiveMatchIndex((prev) =>
        found.length ? Math.min(prev, found.length - 1) : 0,
      );
    }, []);

    // Continuous-scroll mode: re-scan for rendered <mark> highlights whenever
    // the search term (or the set of rendered pages) changes. The text layer
    // renders asynchronously per page, so a MutationObserver (rather than a
    // one-shot pass) is needed to pick up highlights as they land.
    useEffect(() => {
      if (!searchTerm || pageNumber != null) {
        setSearchMatches([]);
        setActiveMatchIndex(0);
        return;
      }
      const container = containerRef.current;
      if (!container) return;

      let debounceTimer: ReturnType<typeof setTimeout> | null = null;
      const scheduleRecompute = () => {
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(recomputeMatches, 150);
      };

      const observer = new MutationObserver(scheduleRecompute);
      observer.observe(container, { childList: true, subtree: true });
      scheduleRecompute();

      return () => {
        observer.disconnect();
        if (debounceTimer) clearTimeout(debounceTimer);
      };
    }, [searchTerm, pageNumber, numPages, recomputeMatches]);

    // Mark whichever match is active, scroll it into view, and report the
    // current position back to the caller (for the "n/N" counter).
    useEffect(() => {
      searchMatches.forEach((el, i) => {
        el.classList.toggle("pdf-search-mark-active", i === activeMatchIndex);
      });
      searchMatches[activeMatchIndex]?.scrollIntoView({
        block: "center",
        behavior: "smooth",
      });
      onSearchMatchesChange?.({
        total: searchMatches.length,
        current: searchMatches.length ? activeMatchIndex + 1 : 0,
      });
    }, [searchMatches, activeMatchIndex, onSearchMatchesChange]);

    useImperativeHandle(
      ref,
      () => ({
        goToNextMatch: () =>
          setActiveMatchIndex((i) =>
            searchMatches.length ? (i + 1) % searchMatches.length : 0,
          ),
        goToPrevMatch: () =>
          setActiveMatchIndex((i) =>
            searchMatches.length
              ? (i - 1 + searchMatches.length) % searchMatches.length
              : 0,
          ),
      }),
      [searchMatches],
    );

    return (
      <Box
        ref={containerRef}
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          py: 2,
          width: "100%",
          height: "100%",
          overflow: "auto",
        }}
      >
        {loadError ? (
          <Typography color="error">{loadError}</Typography>
        ) : (
          <Document
            file={url}
            loading={
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                <CircularProgress />
              </Box>
            }
            onLoadSuccess={(pdf) => {
              setNumPages(pdf.numPages);
              onDocumentLoad?.(pdf.numPages);
            }}
            onLoadError={(err) => setLoadError(err.message)}
          >
            {numPages != null &&
              pageWidth != null &&
              (pageNumber != null ? (
                <Box
                  sx={{
                    boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.15)",
                    border: `1px solid ${gray[200]}`,
                    borderRadius: "12px",
                    overflow: "hidden",
                  }}
                >
                  <Page
                    pageNumber={pageNumber}
                    width={pageWidth}
                    onRenderSuccess={onPageRenderSuccess}
                    customTextRenderer={textRenderer}
                    devicePixelRatio={Math.min(
                      window.devicePixelRatio || 1,
                      MAX_DEVICE_PIXEL_RATIO,
                    )}
                  />
                </Box>
              ) : (
                <>
                  {Array.from({ length: numPages }, (_, index) => {
                    const page = index + 1;
                    return (
                      <Box
                        key={index}
                        ref={(el: HTMLDivElement | null) => {
                          pageRefs.current[page] = el;
                        }}
                        data-page-number={page}
                        sx={{
                          mb: 2,
                          boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.15)",
                          border: `1px solid ${gray[200]}`,
                          borderRadius: "12px",
                          overflow: "hidden",
                        }}
                      >
                        <Page
                          pageNumber={page}
                          width={pageWidth}
                          customTextRenderer={textRenderer}
                          devicePixelRatio={Math.min(
                            window.devicePixelRatio || 1,
                            MAX_DEVICE_PIXEL_RATIO,
                          )}
                          onRenderSuccess={
                            page === numPages
                              ? () => setLastPageRendered(true)
                              : undefined
                          }
                        />
                      </Box>
                    );
                  })}
                  <div ref={sentinelRef} style={{ height: 1 }} />
                </>
              ))}
          </Document>
        )}
      </Box>
    );
  },
);

PdfViewer.displayName = "PdfViewer";
